/**
 * Subscription Plan Card Component
 * 
 * Displays a subscription plan (membership or pass) with a colored header
 * and white content area. Used in subscription selection grids.
 * 
 * **Features:**
 * - Colored gradient header with plan name
 * - White content area with description and price
 * - Arrow CTA button
 * - Immediate action on click (no selection state)
 * - Accessible click area with keyboard support
 * - Responsive design
 * 
 * **Design:**
 * Based on Figma design with gradient headers:
 * - Diamond: Black gradient (#1F2937 to #4B5563)
 * - Platinum: Gray gradient (#6B7280 to #9CA3AF)
 * - Gold: Gold gradient (#D97706 to #F59E0B)
 * 
 * @module butterUI/components
 */

import { ArrowIcon } from '#icons';
import type { ColorVariant } from '@lgc/database/types';

/**
 * Subscription plan data structure
 */
export interface SubscriptionPlan {
  /** Unique identifier */
  id: string | number;
  
  /** Plan name (displayed in header) */
  name: string;
  
  /** Plan description */
  description: string;
  
  /** Formatted price string */
  price: string;
  
  /** Formatted setup fee string (if applicable) */
  setupFee?: string;
  
  /** Billing frequency (e.g., "mo", "monthly") */
  billingFrequency?: string;
  
  /** Validity in days (for passes) */
  validityDays?: number;
  
  /** Color theme for the card header */
  color?: ColorVariant;
  
  /** Plan type (membership or pass) */
  subscriptionType?: 'membership' | 'pass';
}

/**
 * Props for SubscriptionPlanCard
 */
export interface SubscriptionPlanCardProps {
  /**
   * Subscription plan data to display
   */
  plan: SubscriptionPlan;
  
  /**
   * Callback when card is clicked
   */
  onSelect?: (plan: SubscriptionPlan) => void;
  
  /**
   * Custom class name for container
   */
  className?: string;
  
  showSelectButton?: boolean;
}

/**
 * Get gradient classes based on color theme
 */
function getHeaderGradient(color?: string): string {
  switch (color) {
    case 'diamond':
      return 'bg-gradient-to-br from-gray-800 via-gray-700 to-gray-600';
    case 'platinum':
      return 'bg-gradient-to-br from-gray-500 via-gray-400 to-gray-500';
    case 'gold':
      return 'bg-gradient-to-br from-yellow-600 via-yellow-500 to-yellow-400';
    default:
      return 'bg-gradient-to-br from-brand-600 via-brand-500 to-brand-600';
  }
}

/**
 * Subscription Plan Card
 * 
 * Card component for displaying subscription plans with colored headers.
 * Following inclusive design patterns from https://inclusive-components.design/cards/
 * 
 * The card is a semantic container, the button inside is the interactive element.
 * 
 * @example
 * ```tsx
 * <SubscriptionPlanCard
 *   plan={{
 *     id: 1,
 *     name: 'Diamond',
 *     description: 'Unlimited individual access plan for avid golfers',
 *     price: '$350.00',
 *     billingFrequency: 'mo',
 *     color: 'diamond'
 *   }}
 *   onSelect={(plan) => handleSelectPlan(plan)}
 * />
 * ```
 */
export function SubscriptionPlanCard({
  plan,
  onSelect,
  className = '',
  showSelectButton = false
}: SubscriptionPlanCardProps) {
  const handleSelect = () => {
    onSelect?.(plan);
  };
  
  const headerGradient = getHeaderGradient(plan.color);
  
  return (
    <article
      className={`
        relative overflow-hidden rounded-2xl bg-white
        shadow-sm transition-shadow duration-200
        hover:shadow-md
        ${className}
      `}
    >
      {/* Colored gradient header */}
      <div 
        className={`
          relative h-32 px-6 pt-6 pb-20
          ${headerGradient}
        `}
        data-testid={`subscription-${plan.subscriptionType}-card-header-${plan.name}`}
      >
        {/* Dot pattern overlay (optional decorative element) */}
        <div 
          className="absolute right-0 top-0 h-full w-32 opacity-20"
          style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '8px 8px',
          }}
          aria-hidden="true"
        />
        
        {/* Plan name */}
        <h3 className="relative text-2xl font-semibold text-white" data-testid={`subscription-${plan.subscriptionType}-plan-card-title-${plan.name}`}>
          {plan.name}
        </h3>
      </div>
      
      {/* White content area with curved top */}
      <div className="relative -mt-16 rounded-t-3xl bg-white px-6 pb-6 pt-6">
        {/* Description */}
        <p className="mb-4 min-h-[3rem] text-sm text-fg-quaternary">
          {plan.description}
        </p>
        
        {/* Price and CTA */}
        <div className="flex items-center justify-between">
          {/* Price */}
          <p className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-fg-primary" data-testid={`subscription-${plan.subscriptionType}-plan-card-price`}>
              {plan.price}
            </span>
            {plan.billingFrequency && (
              <>
                <span 
                  className="text-sm text-fg-quaternary"
                  aria-hidden="true"
                >
                  /{plan.billingFrequency}
                </span>
                <span className="sr-only">
                  per {plan.billingFrequency}
                </span>
              </>
            )}
            {plan.validityDays && (
              <span className="text-sm text-fg-quaternary">
                for {plan.validityDays} days
              </span>
            )}
          </p>
          
          {/* Arrow CTA button */}
          { showSelectButton &&
          <button
            onClick={handleSelect}
            className="
              flex h-10 w-10 items-center justify-center rounded-full
              bg-brand-600 text-white transition-colors
              hover:bg-brand-700 focus:outline-none focus:ring-2 
              focus:ring-brand-600 focus:ring-offset-2
            "
            aria-label={`Select ${plan.name} plan`}
            data-testid={`subscription-${plan.subscriptionType}-plan-card-select-button-${plan.name}`}
          >
            <ArrowIcon className="h-5 w-5" />
          </button>
          }
        </div>
      </div>
    </article>
  );
}
